import React, { useEffect, useState, useCallback } from 'react'
import AddEventForm from 'components/forms/addEventFormBasic'
import AddPlaceForm from 'components/forms/addPlaceForm'
import ApiSingCard from 'components/post/singCard/apiCard'
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress'; // Import the spinner component
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { styled } from '@mui/system';

const AddEventFormMemo = React.memo(AddEventForm);
const AddPlaceFormMemo = React.memo(AddPlaceForm);

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-container': {
        alignItems: 'flex-start',
    },
}));

const EditDialog = ({
    buttonLabel = null,
    defaultWidth = 'md',
    isLoggedIn = false,
    isOpen = true,
    hasError = false,
    setOpen = () => { },
    formCallback = () => { },
    formType = 'event',
    message = null,
    eventCurrent = null,
    eventCurrentIndex = null,
    placeCurrent = null,
    placeCurrentIndex = null,
    handleNextCard = () => { },
    handleCloseCallback = () => { },
    handleSubmitCallback = () => { },
    showButtons = true,
    showPreview = true,
    showForwardBack = true,
    eventLink = '', // New prop for the event link
    themeName = 'vibemap',
    ...props
}) => {
    const [submitPressed, setSubmitPressed] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [iframeError, setIframeError] = useState(false)

    const handleSubmitButtonClick = async (e) => {
        e.preventDefault(); // Don't click through or under

        setSubmitPressed(true);
        setIsSubmitting(true);
    };

    const handleClickOpen = () => {
        setOpen(true)
    };

    const handleClose = () => {
        setOpen(false)
        handleCloseCallback()
    };

    const handleForm = async (event) => {
        event.preventDefault();
        setSubmitPressed(true);
    };

    const goBack = useCallback(() => {
        const indexNew = eventCurrentIndex - 1;
        handleNextCard(indexNew);
    }, [eventCurrentIndex, handleNextCard]);

    const goForward = useCallback(() => {
        const indexNew = eventCurrentIndex + 1;
        handleNextCard(indexNew);
    }, [eventCurrentIndex, handleNextCard]);

    const eventID = eventCurrent?.id

    const dialogRef = React.useRef(null);
    const dialogWidth = dialogRef?.current?.offsetWidth || defaultWidth;

    const dialogStyle = {
        alignItems: 'flex-start',
        position: 'fixed',
        top: '6%',
        bottom: '80%', // ajust to fit tall iframe
        minHeight: '80vh',
    }

    return (
        <React.Fragment>
            <StyledDialog
                key={isOpen ? 'dialog-open' : 'dialog-closed'}
                open={isOpen}
                onClose={handleClose}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleForm,
                }}
                style={dialogStyle}
                maxWidth={showPreview ? 'xl' : 'md'}
            >
                <div>
                    {showForwardBack ?
                        <>
                            <Button type="button" onClick={goBack} style={{ position: 'absolute', top: 8, left: 8 }}>
                                Previous
                            </Button>
                            <Button type="button" onClick={goForward} style={{ position: 'absolute', top: 8, left: 48 }}>
                                Next
                            </Button>
                        </>
                        : null}

                    <Button type="button" onClick={handleClose} style={{ position: 'absolute', top: 8, right: 8 }}>
                        Close
                    </Button>
                </div>

                <DialogContent ref={dialogRef} style={{ padding: 0 }}>
                    <div style={{ display: 'flex', flexDirection: showPreview ? 'row' : 'column' }}>
                        <div style={{ flex: 2 }}>
                            {formType === 'event'
                                ? <AddEventFormMemo
                                    inDialog
                                    updateOnly
                                    showButtons={false}
                                    showSearch={false}
                                    onCancel={handleClose}
                                    submitPressed={submitPressed}
                                    onSubmitCallback={handleSubmitCallback}
                                    onSuccess={handleClose}
                                    maxWidth={dialogWidth}
                                    themeName={themeName} />
                                : <AddPlaceFormMemo
                                    inDialog={false}
                                    isLoggedIn={isLoggedIn}
                                    updateOnly
                                    doSearch={false}
                                    showSearch={false}
                                    showButtons={false}
                                    submitPressed={submitPressed}
                                    onCancel={handleClose}
                                    onSubmitCallback={handleSubmitCallback}
                                    maxWidth={dialogWidth}
                                    themeName={themeName} />
                            }
                        </div>
                        {showPreview && (
                            <div style={{ flex: 1, marginLeft: '20px' }}>
                                <div style={{ display: 'inline-block', paddingTop: '20px' }}>
                                    <h5>Preview</h5>
                                    {eventID ? (
                                        <div style={{
                                            maxHeight: '350px',
                                            width: '100%',
                                            transform: 'scale(0.9)',
                                            transformOrigin: '0 0',
                                        }}>
                                            <ApiSingCard
                                                key={eventID}
                                                orientation="vertical"
                                                uuid={eventID}
                                                isEvent={true}
                                                type="event"
                                                clearCache={true}
                                            />
                                        </div>
                                    ) : null}
                                    <div>
                                        <h5>Details Link</h5>
                                        {iframeError ? (
                                            <p style={{ color: 'red' }}>Unable to load the preview. The site may have restrictions on embedding.</p>
                                        ) : (
                                            <iframe
                                                src={eventCurrent?.properties?.url}
                                                style={{
                                                    width: '440px',
                                                    height: '750px',
                                                    border: 'none',
                                                    transform: 'scale(0.8)', // 1 / 2 = 0.5
                                                    transformOrigin: '0 0',
                                                }}
                                                title="Event Preview"
                                                onError={() => setIframeError(true)}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
                {showButtons ? (
                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            onClick={handleSubmitButtonClick}
                            color="primary"
                            variant="contained"
                            disabled={isSubmitting} // Disable button while submitting
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" /> // Show spinner
                            ) : (
                                'Submit' // Show button text
                            )}
                        </Button>
                    </DialogActions>
                ) : null}
            </StyledDialog>
        </React.Fragment>
    );
}

export default EditDialog